import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { normalizarTexto } from 'src/app/helpers/busca-eventos.helper';
import { lucideDownload } from '@ng-icons/lucide';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { BarraFiltrosComponent } from 'src/app/shared/filtros-dinamicos';
import {
  diaParaValorFiltro,
  horaParaValorFiltro,
  ladosDoPeriodo,
  modoDoValor,
  ORDENACOES,
  valorFiltroParaHora,
  type CampoBusca,
  type ChipLista,
  type ChipPeriodo,
  type FiltrosDinamicos,
  type ModoPeriodo,
  type OpcaoFiltro,
} from './checkin-filtros';

/** Quantos chips do catálogo dinâmico (período + listas) cabem soltos na linha antes de colapsar. */
const LIMITE_CHIPS_DINAMICOS_VISIVEIS = 2;

/**
 * A partir de quantas opções o dropdown de lista ganha campo de busca. Abaixo
 * disso a lista inteira cabe na tela e o campo só rouba uma linha.
 */
const MINIMO_OPCOES_PARA_BUSCA = 8;

/**
 * Teclas que o campo de busca deixa subir pro CdkMenu: fechar e navegar
 * continuam sendo do menu. Todo o resto (as letras) é do campo.
 */
const TECLAS_DO_MENU = ['Escape', 'ArrowDown', 'ArrowUp', 'Tab'];

interface PeriodoAlterado {
  path: string;
  gte: string | null;
  lte: string | null;
}

interface ListaAlterada {
  path: string;
  valor: string | null;
}

type ChipDinamico = { tipo: 'periodo'; dado: ChipPeriodo } | { tipo: 'lista'; dado: ChipLista };

/**
 * Barra de ferramentas da tela de Check-ins: busca, filtros pré-definidos e
 * exportação.
 *
 * Densidade no formato Stripe: chips de 32px e busca de 32px, tudo numa linha
 * só. A versão anterior usava botões de 40px e a barra quebrava em duas linhas,
 * deixando a busca com ~190px — truncando o próprio placeholder.
 *
 * Sessão e ordenação continuam fixos (o backend só aceita `filtro`, `sessaoId`
 * e o `Pageable`). Período, lote e status (GUE-391, ADR-0019) vêm do catálogo
 * dinâmico de `GET .../ingressos/filtros` — o pai traduz com
 * `filtrosParaChips` e passa aqui; nenhum nome de filtro é hardcoded neste
 * componente, nem o VALOR aplicado (`filtros`, chaveado por `path`). Um
 * filtro novo do mesmo TIPO (`date` ou `enumerated`) não pede mudança nenhuma
 * aqui — é exatamente o "segue um padrão de tipos suportados" da issue.
 * Além de sessão+ordenar já existentes na linha, só cabem
 * {@link LIMITE_CHIPS_DINAMICOS_VISIVEIS} chips do catálogo soltos — o resto
 * colapsa no chip "Mais filtros", pra a barra não quebrar linha quando o
 * catálogo crescer.
 *
 * O chip de sessão só aparece quando o pai passa a lista (evento sem sessão
 * configurada não ganha um filtro vazio). Mesma regra pros chips dinâmicos:
 * um catálogo sem `loteId`, por exemplo, não deixa a lista pela metade.
 *
 * Sem debounce aqui: quem controla o disparo é o pai (`list.setSearch`).
 *
 * A barra de busca aceita prefixos (`lote:`, `nome:`) derivados do próprio
 * catálogo. Como o filtro dinâmico funciona no fio — a gramática
 * `campo[operador]=valor`, o vocabulário de operador aceito e a regra que faz
 * do `label` do backend o prefixo digitável — está documentado no cabeçalho de
 * `checkin-filtros.ts`.
 */
@Component({
  selector: 'app-checkin-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, NgTemplateOutlet, HlmDropdownMenuImports, BarraFiltrosComponent],
  providers: [provideIcons({ lucideDownload })],
  // O host é um elemento a mais entre o slot projetado e o flex da toolbar.
  // Sem `flex-1` ele encolhe para o tamanho do conteúdo, a barra quebra em duas
  // linhas e a busca fica com ~190px, truncando o placeholder.
  host: { class: 'flex flex-1 items-center gap-2' },
  template: `
    <!-- Densidade Stripe: chips de 32px, não botões de 40. A barra inteira cabe
         numa linha com a busca, que é o que o produtor usa 90% do tempo.

         A barra de busca com prefixos vive em shared/filtros-dinamicos: era
         idêntica à de meus eventos, e duas cópias significavam a mesma
         interação divergindo a cada correção. O formulário de período continua
         declarado AQUI porque é o mesmo corpo do chip e do menu "Mais
         filtros" — um formulário só é o que garante que os três caminhos
         apliquem exatamente o mesmo filtro. -->
    <app-barra-filtros
      [busca]="busca()"
      [campos]="campos()"
      [filtros]="filtros()"
      [corpoPeriodo]="corpoPeriodo"
      placeholder="Buscar por nome, e-mail, CPF ou código"
      rotuloAcessivel="Buscar ingressos ou digitar um filtro"
      (buscaChange)="buscaChange.emit($event)"
      (listaChange)="listaChange.emit($event)"
      (textoChange)="textoChange.emit($event)"
      (periodoChange)="periodoChange.emit($event)"
    />

    @if (sessoes().length > 0) {
      <button type="button" [hlmDropdownMenuTrigger]="menuSessao" align="start" [class]="classeChip(!!sessaoSelecionada())">
        <span class="text-base leading-none">{{ sessaoSelecionada() ? '' : '&#8853;' }}</span>
        {{ rotuloSessaoAtiva() }}
      </button>
      <ng-template #menuSessao>
        <hlm-dropdown-menu class="admin-portal-scope max-h-80">
          <hlm-dropdown-menu-group>
            <button type="button" hlmDropdownMenuRadio [checked]="!sessaoSelecionada()" (triggered)="sessaoChange.emit(null)">
              Todas as sessões
            </button>
            @for (s of sessoes(); track s.valor) {
              <button type="button" hlmDropdownMenuRadio [checked]="sessaoSelecionada() === s.valor" (triggered)="sessaoChange.emit(s.valor)">
                {{ s.rotulo }}
              </button>
            }
          </hlm-dropdown-menu-group>
        </hlm-dropdown-menu>
      </ng-template>
    }

    <button type="button" [hlmDropdownMenuTrigger]="menuOrdem" align="start" [class]="classeChip(true)">
      {{ rotuloOrdemAtiva() }}
    </button>
    <ng-template #menuOrdem>
      <hlm-dropdown-menu class="admin-portal-scope">
        <hlm-dropdown-menu-group>
          @for (o of ordenacoes(); track o.valor) {
            <button type="button" hlmDropdownMenuRadio [checked]="ordem() === o.valor" (triggered)="ordemChange.emit(o.valor)">
              {{ o.rotulo }}
            </button>
          }
        </hlm-dropdown-menu-group>
      </hlm-dropdown-menu>
    </ng-template>

    <!-- max-h-80 nos menus alimentados pelo servidor (sessão, lista, mais
         filtros): o CdkMenuTrigger abre o overlay sem maxHeight nenhum, e o
         único teto que existe é o max-height:100% do .cdk-overlay-pane. Com 40
         lotes, medido: o menu ia de 4px a 796px numa viewport de 800 (e 4 a 808
         em 375x812) — rolava, mas ocupava a tela inteira saindo de um chip de
         32px. Os menus de tamanho fixo (ordenar, período) ficam sem teto.

         GUE-391: chips do catálogo dinâmico que couberam soltos na linha. -->
    @for (chip of chipsVisiveis(); track chip.dado.path) {
      @if (chip.tipo === 'periodo') {
        <button type="button" [hlmDropdownMenuTrigger]="menuPeriodo" align="start" [class]="classeChip(temValorPeriodo(chip.dado.path))">
          <span class="text-base leading-none">{{ temValorPeriodo(chip.dado.path) ? '' : '&#8853;' }}</span>
          {{ chip.dado.rotulo }}
        </button>
        <ng-template #menuPeriodo>
          <hlm-dropdown-menu class="admin-portal-scope">
            <div class="p-2">
              <ng-container [ngTemplateOutlet]="corpoPeriodo" [ngTemplateOutletContext]="{ $implicit: chip.dado }" />
            </div>
          </hlm-dropdown-menu>
        </ng-template>
      } @else {
        <button type="button" [hlmDropdownMenuTrigger]="menuLista" align="start" (hlmDropdownMenuOpened)="focarBusca()" [class]="classeChip(!!valorLista(chip.dado.path))">
          <span class="text-base leading-none">{{ valorLista(chip.dado.path) ? '' : '&#8853;' }}</span>
          {{ rotuloLista(chip.dado) }}
        </button>
        <!-- Opções escritas AQUI, e não por ngTemplateOutlet: o outlet
             instancia a view com o injetor do ponto de DECLARAÇÃO do
             ng-template, não o do ponto de inserção. Um corpo declarado na
             raiz do componente fica fora do cdkMenu, e cada
             hlmDropdownMenuRadio (= CdkMenuItem) morre com NG0201 pedindo
             MENU_STACK — o overlay abre e nunca chega a ser posicionado.
             Vale para qualquer diretiva de menu do CDK; corpoPeriodo segue
             em outlet porque só tem input e button comuns. -->
        <ng-template #menuLista>
          <hlm-dropdown-menu class="admin-portal-scope max-h-80">
            @if (temBuscaLista(chip.dado)) {
              <!-- Fora do hlm-dropdown-menu-group de propósito: nao e um item de menu.
                   O keydown para aqui (aoTeclarBusca) porque o CdkMenu escuta keydown
                   no proprio menu pro typeahead — sem isso, digitar "pa" move o foco
                   pro item que comeca com P em vez de escrever no campo. -->
              <div class="sticky top-0 z-10 -mx-1 -mt-1 mb-1 bg-popover p-1">
                <input
                  #campoBusca
                  type="search"
                  class="h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  [placeholder]="'Buscar ' + chip.dado.rotulo.toLowerCase()"
                  [attr.aria-label]="'Buscar ' + chip.dado.rotulo.toLowerCase()"
                  [value]="termoLista(chip.dado.path)"
                  (input)="buscaLista.set({ path: chip.dado.path, termo: $any($event.target).value })"
                  (keydown)="aoTeclarBusca($event)"
                />
              </div>
            }
            <hlm-dropdown-menu-group>
              <button type="button" hlmDropdownMenuRadio [checked]="!valorLista(chip.dado.path)" (triggered)="listaChange.emit({ path: chip.dado.path, valor: null })">
                Todos
              </button>
              @for (o of opcoesLista(chip.dado); track o.valor) {
                <button type="button" hlmDropdownMenuRadio [checked]="valorLista(chip.dado.path) === o.valor" (triggered)="listaChange.emit({ path: chip.dado.path, valor: o.valor })">
                  {{ o.rotulo }}
                </button>
              } @empty {
                <p class="m-0 px-2 py-1.5 text-sm text-muted-foreground">Nada com «{{ termoLista(chip.dado.path) }}».</p>
              }
            </hlm-dropdown-menu-group>
          </hlm-dropdown-menu>
        </ng-template>
      }
    }

    @if (chipsColapsados().length > 0) {
      <button type="button" [hlmDropdownMenuTrigger]="menuMaisFiltros" align="start" (hlmDropdownMenuOpened)="focarBusca()" [class]="classeChip(temChipColapsadoAtivo())">
        <span class="text-base leading-none">{{ temChipColapsadoAtivo() ? '' : '&#8853;' }}</span>
        Mais filtros
      </button>
      <ng-template #menuMaisFiltros>
        <hlm-dropdown-menu class="admin-portal-scope max-h-80">
          @for (chip of chipsColapsados(); track chip.dado.path) {
            <div class="border-b border-border p-2 last:border-b-0">
              <p class="mb-1 px-1 text-xs font-semibold text-muted-foreground">{{ chip.dado.rotulo }}</p>
              @if (chip.tipo === 'periodo') {
                <ng-container [ngTemplateOutlet]="corpoPeriodo" [ngTemplateOutletContext]="{ $implicit: chip.dado }" />
              } @else {
                <!-- Mesma regra do chip solto: nada de outlet com item de menu do CDK. -->
                @if (temBuscaLista(chip.dado)) {
                  <!-- Fora do hlm-dropdown-menu-group de propósito: nao e um item de menu.
                       O keydown para aqui (aoTeclarBusca) porque o CdkMenu escuta keydown
                       no proprio menu pro typeahead — sem isso, digitar "pa" move o foco
                       pro item que comeca com P em vez de escrever no campo. -->
                  <div class="sticky top-0 z-10 -mx-1 -mt-1 mb-1 bg-popover p-1">
                    <input
                      #campoBusca
                      type="search"
                      class="h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      [placeholder]="'Buscar ' + chip.dado.rotulo.toLowerCase()"
                      [attr.aria-label]="'Buscar ' + chip.dado.rotulo.toLowerCase()"
                      [value]="termoLista(chip.dado.path)"
                      (input)="buscaLista.set({ path: chip.dado.path, termo: $any($event.target).value })"
                      (keydown)="aoTeclarBusca($event)"
                    />
                  </div>
                }
                <hlm-dropdown-menu-group>
                  <button type="button" hlmDropdownMenuRadio [checked]="!valorLista(chip.dado.path)" (triggered)="listaChange.emit({ path: chip.dado.path, valor: null })">
                    Todos
                  </button>
                  @for (o of opcoesLista(chip.dado); track o.valor) {
                    <button type="button" hlmDropdownMenuRadio [checked]="valorLista(chip.dado.path) === o.valor" (triggered)="listaChange.emit({ path: chip.dado.path, valor: o.valor })">
                      {{ o.rotulo }}
                    </button>
                  } @empty {
                    <p class="m-0 px-2 py-1.5 text-sm text-muted-foreground">Nada com «{{ termoLista(chip.dado.path) }}».</p>
                  }
                </hlm-dropdown-menu-group>
              }
            </div>
          }
        </hlm-dropdown-menu>
      </ng-template>
    }

    <!-- Corpo compartilhado do chip de período: dois modos (dia/intervalo de
         dias OU faixa de horas — decisão 3 do ADR-0019), cada um com dois
         campos nativos (sem lib de calendário — <input type="date"> e
         <input type="datetime-local"> já cobrem o piloto). -->
    <ng-template #corpoPeriodo let-chip>
      <div class="flex w-64 flex-col gap-2">
        <div class="flex gap-1 rounded-md bg-muted p-0.5">
          <button
            type="button"
            class="flex-1 rounded px-2 py-1 text-xs font-semibold"
            [class.bg-card]="modoAtivo(chip.path) === 'dia'"
            [class.text-foreground]="modoAtivo(chip.path) === 'dia'"
            [class.text-muted-foreground]="modoAtivo(chip.path) !== 'dia'"
            (click)="modoEscolhido.set({ path: chip.path, modo: 'dia' })"
          >
            Dia
          </button>
          <button
            type="button"
            class="flex-1 rounded px-2 py-1 text-xs font-semibold"
            [class.bg-card]="modoAtivo(chip.path) === 'horas'"
            [class.text-foreground]="modoAtivo(chip.path) === 'horas'"
            [class.text-muted-foreground]="modoAtivo(chip.path) !== 'horas'"
            (click)="modoEscolhido.set({ path: chip.path, modo: 'horas' })"
          >
            Faixa de horas
          </button>
        </div>

        @if (modoAtivo(chip.path) === 'dia') {
          <label class="text-xs text-muted-foreground">
            De
            <input
              type="date"
              class="mt-0.5 h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground"
              [value]="valorDia(chip.path, 'gte')"
              (change)="aoMudarDia(chip.path, 'gte', $any($event.target).value)"
            />
          </label>
          <label class="text-xs text-muted-foreground">
            Até
            <input
              type="date"
              class="mt-0.5 h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground"
              [value]="valorDia(chip.path, 'lte')"
              (change)="aoMudarDia(chip.path, 'lte', $any($event.target).value)"
            />
          </label>
        } @else {
          <label class="text-xs text-muted-foreground">
            De
            <input
              type="datetime-local"
              class="mt-0.5 h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground"
              [value]="valorHora(chip.path, 'gte')"
              (change)="aoMudarHora(chip.path, 'gte', $any($event.target).value)"
            />
          </label>
          <label class="text-xs text-muted-foreground">
            Até
            <input
              type="datetime-local"
              class="mt-0.5 h-8 w-full rounded-input border border-border bg-card px-2 text-sm text-foreground"
              [value]="valorHora(chip.path, 'lte')"
              (change)="aoMudarHora(chip.path, 'lte', $any($event.target).value)"
            />
          </label>
        }

        @if (temValorPeriodo(chip.path)) {
          <button type="button" class="self-start text-xs font-semibold text-muted-foreground hover:text-foreground" (click)="periodoChange.emit({ path: chip.path, gte: null, lte: null })">
            Limpar período
          </button>
        }
      </div>
    </ng-template>

    @if (temFiltroAtivo()) {
      <button type="button" class="h-[2rem] shrink-0 px-2 text-sm font-semibold text-muted-foreground hover:text-foreground" (click)="limparTudo()">
        Limpar filtros
      </button>
    }

    <!-- Exportar voltou. A objeção que o escondeu era o CSV não responder
         "quem entrou em qual janela"; a resposta não é agrupar o arquivo, é o
         filtro de DATA do catálogo dinâmico — filtra a janela na tela e
         exporta o que está na tela. Isso só vale porque a exportação passou a
         mandar os MESMOS filtros da lista (ver paramsExportacao); antes ela
         mandava só o termo de busca, e o arquivo saía sem filtro nenhum.

         ml-auto e não um espaçador flex-1: o espaçador engolia a folga e
         travava a busca em ~400px; com ml-auto quem cresce é a busca.
         (Sem crase neste comentário: ele vive dentro de um template literal.) -->
    @if (podeExportar()) {
      <button
        type="button"
        [class]="classeChip(false) + ' ml-auto'"
        [disabled]="exportando()"
        (click)="exportar.emit()"
      >
        <ng-icon name="lucideDownload" size="14px" />
        {{ exportando() ? 'Exportando…' : 'Exportar' }}
      </button>
    }
  `,
})
export class CheckinToolbarComponent {
  /**
   * Ordenações oferecidas. Vêm do catálogo de `/meta` pela página; o default é
   * a lista local, para o chip não nascer vazio enquanto a resposta não chega.
   */
  readonly ordenacoes = input<OpcaoFiltro[]>(ORDENACOES);

  readonly busca = input<string>('');
  /** Vazio esconde o chip de sessão (evento sem sessão configurada). */
  readonly sessoes = input<OpcaoFiltro[]>([]);
  readonly sessaoSelecionada = input<string | null>(null);
  readonly ordem = input<string>(ORDENACOES[0].valor);
  readonly exportando = input<boolean>(false);
  /**
   * Exportar leva dados pessoais dos participantes embora, então não é do
   * operador de portaria: só gerente, sócio/dono do evento e admin. O pai
   * resolve pela ability; aqui só se ESCONDE, porque um botão que só falha
   * ao clicar é pior que botão nenhum.
   */
  readonly podeExportar = input<boolean>(false);

  /** GUE-391: catálogo dinâmico traduzido pelo pai via `filtrosParaChips`. */
  readonly filtroPeriodo = input<ChipPeriodo | null>(null);
  readonly filtrosLista = input<ChipLista[]>([]);
  /**
   * O MESMO catálogo, na forma que a barra de busca usa (`camposDeBusca`):
   * inclui os filtros textuais, que não viram chip, e traz o prefixo de cada
   * campo. Vazio = barra se comporta exatamente como o campo de busca antigo.
   */
  readonly campos = input<CampoBusca[]>([]);
  /** Filtros aplicados agora, chaveados por `path` — ver `FiltrosDinamicos`. */
  readonly filtros = input<FiltrosDinamicos>({});

  /** O pai faz o debounce. */
  readonly buscaChange = output<string>();
  readonly sessaoChange = output<string | null>();
  readonly ordemChange = output<string>();
  readonly listaChange = output<ListaAlterada>();
  readonly periodoChange = output<PeriodoAlterado>();
  readonly limpar = output<void>();
  readonly exportar = output<void>();
  /** Filtro textual aplicado pela barra (`nome:maria`); `valor: null` remove. */
  readonly textoChange = output<ListaAlterada>();

  /**
   * Modo escolhido NESTE fechamento do chip de período, por `path` — sem
   * isto, trocar pra "Faixa de horas" com os campos vazios não teria onde se
   * sustentar (o modo derivado do valor cru cairia sempre em "dia" até o
   * usuário preencher algo). Ausente = segue o valor cru atual.
   */
  readonly modoEscolhido = signal<{ path: string; modo: ModoPeriodo } | null>(null);

  modoAtivo(path: string): ModoPeriodo {
    const escolhido = this.modoEscolhido();
    if (escolhido?.path === path) return escolhido.modo;
    const lados = ladosDoPeriodo(this.filtros()[path]);
    return modoDoValor(lados.gte || lados.lte || null);
  }

  /** `eq` (dia inteiro) aparece nos dois campos — ver {@link ladosDoPeriodo}. */
  valorDia(path: string, operador: 'gte' | 'lte'): string {
    return this.modoAtivo(path) === 'dia' ? ladosDoPeriodo(this.filtros()[path])[operador] : '';
  }

  /**
   * Lê pelo mesmo caminho do dia ({@link ladosDoPeriodo}): para `gte`/`lte` dá
   * exatamente o valor cru, e um `eq` que tenha chegado com hora (só por URL
   * editada à mão — a UI nunca monta isso, e o backend responde 400) aparece
   * nos campos em vez de deixar o chip aceso sobre um formulário vazio.
   */
  valorHora(path: string, operador: 'gte' | 'lte'): string {
    return this.modoAtivo(path) === 'horas'
      ? valorFiltroParaHora(ladosDoPeriodo(this.filtros()[path])[operador] || null)
      : '';
  }

  temValorPeriodo(path: string): boolean {
    const lados = ladosDoPeriodo(this.filtros()[path]);
    return !!lados.gte || !!lados.lte;
  }

  private emitirPeriodo(path: string, operador: 'gte' | 'lte', valorCru: string | null): void {
    const atuais = this.filtros()[path] ?? {};
    const gte = operador === 'gte' ? valorCru : (atuais['gte'] ?? null);
    const lte = operador === 'lte' ? valorCru : (atuais['lte'] ?? null);
    this.periodoChange.emit({ path, gte, lte });
  }

  aoMudarDia(path: string, operador: 'gte' | 'lte', valor: string): void {
    this.emitirPeriodo(path, operador, valor ? diaParaValorFiltro(valor) : null);
  }

  aoMudarHora(path: string, operador: 'gte' | 'lte', valor: string): void {
    this.emitirPeriodo(path, operador, valor ? horaParaValorFiltro(valor) : null);
  }

  valorLista(path: string): string | null {
    return this.filtros()[path]?.['eq'] ?? null;
  }

  /**
   * Termo digitado NESTE fechamento do chip de lista, por `path` — mesma forma
   * de `modoEscolhido`. Não vai pro `filtros` porque não é filtro do servidor:
   * é só o que está visível no dropdown agora.
   */
  readonly buscaLista = signal<{ path: string; termo: string } | null>(null);

  termoLista(path: string): string {
    const busca = this.buscaLista();
    return busca?.path === path ? busca.termo : '';
  }

  temBuscaLista(chip: ChipLista): boolean {
    return chip.opcoes.length >= MINIMO_OPCOES_PARA_BUSCA;
  }

  opcoesLista(chip: ChipLista): OpcaoFiltro[] {
    const termo = normalizarTexto(this.termoLista(chip.path));
    if (!termo) return chip.opcoes;
    return chip.opcoes.filter((o) => normalizarTexto(o.rotulo).includes(termo));
  }

  aoTeclarBusca(evento: KeyboardEvent): void {
    if (!TECLAS_DO_MENU.includes(evento.key)) evento.stopPropagation();
  }

  private readonly campoBusca = viewChild<ElementRef<HTMLInputElement>>('campoBusca');

  /**
   * Manda o foco pro campo assim que o menu abre — sem isto o CdkMenuTrigger
   * foca o primeiro item ("Todos") e as letras caem no typeahead do menu, o
   * que anula o campo. O queueMicrotask existe porque o trigger chama
   * focusFirstItem SÍNCRONO logo depois de anexar o overlay: focar antes disso
   * seria desfeito. Um menu aberto de cada vez (o CDK fecha os irmãos), então
   * a query só encontra o campo que está aberto agora.
   */
  focarBusca(): void {
    queueMicrotask(() => this.campoBusca()?.nativeElement.focus());
  }

  rotuloLista(chip: ChipLista): string {
    const valor = this.valorLista(chip.path);
    return valor ? (chip.opcoes.find((o) => o.valor === valor)?.rotulo ?? chip.rotulo) : chip.rotulo;
  }

  private readonly chipsDinamicos = computed<ChipDinamico[]>(() => {
    const out: ChipDinamico[] = [];
    const periodo = this.filtroPeriodo();
    if (periodo) out.push({ tipo: 'periodo', dado: periodo });
    for (const lista of this.filtrosLista()) out.push({ tipo: 'lista', dado: lista });
    return out;
  });

  readonly chipsVisiveis = computed(() =>
    this.chipsDinamicos().slice(0, LIMITE_CHIPS_DINAMICOS_VISIVEIS),
  );
  readonly chipsColapsados = computed(() =>
    this.chipsDinamicos().slice(LIMITE_CHIPS_DINAMICOS_VISIVEIS),
  );

  readonly temChipColapsadoAtivo = computed(() =>
    this.chipsColapsados().some((chip) =>
      chip.tipo === 'periodo' ? this.temValorPeriodo(chip.dado.path) : !!this.valorLista(chip.dado.path),
    ),
  );

  /**
   * `@ViewChild` de decorator, e não `viewChild()`: a query por signal nunca
   * resolve no JIT do vitest deste repo (ver `src/app/testing/definir-input.ts`),
   * e este é o caminho que precisa ser testado.
   */
  @ViewChild(BarraFiltrosComponent) private readonly barra?: BarraFiltrosComponent;

  /**
   * A barra é dona do próprio texto e só é semeada pelo `busca` no ngOnInit —
   * então limpar filtros aqui não a esvaziaria sozinho, e ficaria um termo
   * escrito sobre uma lista que não está mais filtrada por ele. Mesma regra de
   * meus eventos: uma barra de filtros que limpa pela metade é pior que uma
   * que não limpa, porque some com o motivo e deixa a causa na tela.
   */
  limparTudo(): void {
    this.barra?.limparTexto();
    this.limpar.emit();
  }

  /** A primeira do catálogo é o default da tela — não uma constante local. */
  readonly ordemPadrao = computed(() => this.ordenacoes()[0]?.valor ?? ORDENACOES[0].valor);
  readonly ordemAlterada = computed(() => this.ordem() !== this.ordemPadrao());
  readonly temFiltroAtivo = computed(
    () =>
      !!this.sessaoSelecionada() ||
      this.ordemAlterada() ||
      Object.keys(this.filtros()).some((path) => Object.keys(this.filtros()[path]).length > 0),
  );
  readonly filtrosAtivos = computed(
    () => (this.sessaoSelecionada() ? 1 : 0) + (this.ordemAlterada() ? 1 : 0),
  );

  readonly rotuloSessaoAtiva = computed(() => {
    const id = this.sessaoSelecionada();
    return this.sessoes().find((s) => s.valor === id)?.rotulo ?? 'Sessão';
  });

  /**
   * O chip de ordenação está SEMPRE aceso, ao contrário dos chips de filtro.
   *
   * Filtro tem estado nulo: sem valor o param sai da query e o `⊕` cinza diz
   * "nada aplicado". Ordenação não tem — a lista sempre volta ordenada por
   * alguma coisa, e o padrão é uma ordem em vigor, não a ausência de uma.
   * Pintar o padrão de cinza afirmava que nada estava escolhido.
   *
   * O rótulo é sempre a ordenação corrente, então é ele que responde "em que
   * ordem estou vendo isso?" sem abrir o menu. O {@link ordemAlterada} continua
   * existindo e continua significando "saiu do padrão" — é dele que dependem o
   * "Limpar filtros" e a contagem de filtros ativos, e ali ordenação CONTA.
   */
  readonly rotuloOrdemAtiva = computed(
    () =>
      this.ordenacoes().find((o) => o.valor === this.ordem())?.rotulo ??
      ORDENACOES.find((o) => o.valor === this.ordem())?.rotulo ??
      this.ordenacoes()[0]?.rotulo ??
      'Ordenar',
  );

  /**
   * Chip no formato Stripe: 32px, borda fina, `⊕` some quando há valor — o
   * próprio rótulo passa a ser o valor escolhido. Um ternário só: duas classes
   * ativa/inativa lado a lado quebram com a inversão de camada do !important.
   */
  classeChip(ativo: boolean): string {
    const base =
      'inline-flex h-[2rem] shrink-0 items-center gap-1.5 rounded-full border px-3 text-sm font-semibold ' +
      'font-portal normal-case tracking-normal cursor-pointer disabled:opacity-45 ';
    return ativo
      ? base + 'border-primary bg-primary/10 text-primary'
      : base + 'border-border bg-card text-muted-foreground hover:border-primary hover:text-primary';
  }
}
